import {
  ArchiveIcon,
  CreditCardIcon,
  FolderOpenIcon,
  type LucideIcon,
  PaletteIcon,
  ReceiptTextIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  UsersIcon,
} from 'lucide-react';
import type { SettingsSection } from '../../entities/org';

export type NavItem = {
  key: string;
  /** Already translated, or a name that is data (a workspace). */
  label: string;
  to: string;
  icon: LucideIcon;
  /** Whether `pathname` belongs to this destination (nested routes included). */
  isActive: (pathname: string) => boolean;
};

/**
 * What a slot gets to know: the 64px rail needs a different rendering from the 240px sidebar, and the
 * mobile drawer has no room beside it for a menu.
 */
export type ShellSlotContext = { collapsed: boolean; inDrawer: boolean; onNavigate?: () => void };
export type ShellSlot = (context: ShellSlotContext) => React.ReactNode;

export function workspacePath(orgSlug: string, workspaceSlug: string): string {
  return `/o/${orgSlug}/w/${workspaceSlug}`;
}

/** One sidebar entry per visible workspace: real destinations only. */
export function workspaceNavItem(
  orgSlug: string,
  workspace: { id: string; slug: string; name: string; archived_at: string | null },
): NavItem {
  const to = workspacePath(orgSlug, workspace.slug);
  return {
    key: workspace.id,
    label: workspace.name,
    to,
    icon: workspace.archived_at ? ArchiveIcon : FolderOpenIcon,
    isActive: (pathname) => pathname === to || pathname.startsWith(`${to}/`),
  };
}

/** The organization's Orders page. The pay page of one order lives under it, so it stays active there. */
export function ordersNavItem(orgSlug: string, label: string): NavItem {
  const to = `/o/${orgSlug}/orders`;
  return {
    key: 'orders',
    label,
    to,
    icon: ReceiptTextIcon,
    isActive: (pathname) => pathname === to || pathname.startsWith(`${to}/`),
  };
}

/** The way into the organization's settings (settings mode of the sidebar). */
export function settingsNavItem(orgSlug: string, label: string): NavItem {
  const base = `/o/${orgSlug}/settings`;
  return {
    key: 'settings',
    label,
    to: `${base}/general`,
    icon: SettingsIcon,
    isActive: (pathname) => pathname === base || pathname.startsWith(`${base}/`),
  };
}

const sectionIcons: Record<SettingsSection, LucideIcon> = {
  general: SlidersHorizontalIcon,
  members: UsersIcon,
  workspaces: FolderOpenIcon,
  billing: CreditCardIcon,
};

/** One settings section in the sidebar's settings mode. Workspaces stays active on a workspace's settings. */
export function settingsSectionNavItem(
  orgSlug: string,
  section: SettingsSection,
  label: string,
): NavItem {
  const to = `/o/${orgSlug}/settings/${section}`;
  return {
    key: `settings-${section}`,
    label,
    to,
    icon: sectionIcons[section],
    isActive: (pathname) => pathname === to || pathname.startsWith(`${to}/`),
  };
}

/** The design reference. Callers add it behind `import.meta.env.DEV` only, so production has no trace of it. */
export function designNavItem(label: string): NavItem {
  return {
    key: 'design',
    label,
    to: '/design',
    icon: PaletteIcon,
    isActive: (pathname) => pathname.startsWith('/design'),
  };
}

/** The way into the staff area. Callers add it only for someone who holds a staff role. */
export function staffAreaNavItem(label: string): NavItem {
  return {
    key: 'staff-area',
    label,
    to: '/admin',
    icon: ShieldCheckIcon,
    isActive: (pathname) => pathname.startsWith('/admin'),
  };
}
